import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { DiskFolderFailure, useDiskFolderClips } from 'modules/match-video/ui/hooks';
import React, { useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { UDDropzone } from 'ud-form/components/image-dropzone';
import UDInput from 'ud-form/components/input/component';
import UDRadio from 'ud-form/components/radio';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

export type ClipSource = 'files' | 'disk';

/** Containers the assembler reads (mediabunny `ALL_FORMATS`); a phone or action camera writes one of them. */
export const CLIP_ACCEPT: Accept = {
  'video/mp4': ['.mp4', '.m4v'],
  'video/quicktime': ['.mov'],
};

/** One clip is seconds to a minute; a gigabyte means a whole recording slipped in, which the review cannot hold. */
export const CLIP_MAX_SIZE = 1024 * 1024 * 1024;

const SOURCE_LABELS: Record<ClipSource, MessageDescriptor> = {
  files: msg`Файлы`,
  disk: msg`Ссылка на папку`,
};

const DISK_FAILURE_MESSAGES: Record<DiskFolderFailure, MessageDescriptor> = {
  album: msg`Это ссылка на альбом. Нужна ссылка на папку: откройте папку на Диске и нажмите «Поделиться».`,
  file: msg`Это ссылка на один файл. Нужна ссылка на папку с клипами.`,
  'not-disk': msg`Это не ссылка на Яндекс.Диск. Нужна ссылка вида disk.yandex.ru/d/…`,
  'not-found': msg`Папка не найдена: ссылку удалили или закрыли доступ.`,
  'not-folder': msg`По ссылке не папка. Нужна ссылка на папку с клипами.`,
  network: msg`Диск не ответил. Проверьте интернет и попробуйте ещё раз.`,
  http: msg`Диск отказал в загрузке. Попробуйте ещё раз.`,
  empty: msg`В папке нет видео MP4 или MOV.`,
};

export interface ClipSourcePickerProps {
  /** Clips from either source, unsorted; the screen adds them to its list. */
  onPick: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Where the review clips come from: files from the computer or a public Yandex Disk folder. Both give `File`, so the
 * rest of the screen does not know the source. Switching the source keeps the clips already added: files and a folder
 * may go into one review.
 */
export const ClipSourcePicker = (props: ClipSourcePickerProps) => {
  const { onPick, disabled } = props;
  const { t } = useLingui();
  const [source, setSource] = useState<ClipSource>('files');

  return (
    <div className="flex flex-col gap-3" data-testid="clip-source-picker">
      <div role="radiogroup" aria-label={t`Откуда клипы`} className="flex flex-wrap gap-4">
        {(Object.keys(SOURCE_LABELS) as ClipSource[]).map((value) => (
          <label key={value} className="flex cursor-pointer items-center gap-2">
            <UDRadio
              name="clip-source"
              value={value}
              checked={source === value}
              disabled={disabled}
              onChange={() => setSource(value)}
            />
            <UDText type="subhead">{t(SOURCE_LABELS[value])}</UDText>
          </label>
        ))}
      </div>
      {source === 'files' ? (
        <ClipFilesZone onPick={onPick} disabled={disabled} />
      ) : (
        <DiskFolderField onPick={onPick} disabled={disabled} />
      )}
    </div>
  );
};

interface ClipSourceFieldProps {
  onPick: (files: File[]) => void;
  disabled?: boolean;
}

const ClipFilesZone = ({ onPick, disabled }: ClipSourceFieldProps) => {
  const { t } = useLingui();
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);

  const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
    setRejectedNames(rejections.map((rejection) => rejection.file.name));
    if (accepted.length > 0) onPick(accepted);
  };

  const { getRootProps, getInputProps, isDragActive, isFocused } = useDropzone({
    accept: CLIP_ACCEPT,
    maxSize: CLIP_MAX_SIZE,
    multiple: true,
    disabled,
    onDrop: handleDrop,
  });
  const rejected = rejectedNames.join(', ');

  return (
    <div className="flex flex-col gap-2">
      <UDDropzone
        previews={[]}
        preview="none"
        rootProps={getRootProps()}
        inputProps={getInputProps()}
        accept={CLIP_ACCEPT}
        maxSize={CLIP_MAX_SIZE}
        isDragActive={isDragActive}
        isFocused={isFocused}
        isDisabled={disabled}
        title={t`Перетащите сюда клипы матча`}
        buttonText={t`Выберите файлы`}
        inputLabel={t`Выберите клипы`}
      />
      {rejected && (
        <UDText type="footnote" role="alert" className="text-CaseErrorText">
          <Trans>Не подошли: {rejected}. Нужны видео MP4 или MOV до 1 ГБ.</Trans>
        </UDText>
      )}
    </div>
  );
};

const DiskFolderField = ({ onPick, disabled }: ClipSourceFieldProps) => {
  const { t } = useLingui();
  const [link, setLink] = useState('');
  const disk = useDiskFolderClips();
  const isBusy = disk.status === 'listing' || disk.status === 'downloading';

  const handleLoad = async () => {
    const files = await disk.load(link);
    if (files) onPick(files);
  };

  const fileNumber = disk.progress?.fileNumber ?? 0;
  const fileCount = disk.progress?.fileCount ?? 0;
  const percent = Math.round((disk.progress?.share ?? 0) * 100);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <UDInput
          value={link}
          onChange={(event) => setLink(event.target.value)}
          placeholder="https://disk.yandex.ru/d/…"
          aria-label={t`Ссылка на папку Яндекс.Диска`}
          disabled={disabled || isBusy}
          hasError={disk.failure !== null}
          wrapperProps={{ className: 'min-w-0 flex-1 basis-80' }}
        />
        <UDButton variant="secondary" type="button" disabled={disabled || isBusy || !link.trim()} onClick={handleLoad}>
          <Trans>Загрузить клипы</Trans>
        </UDButton>
        {isBusy && (
          <UDButton variant="secondary" type="button" onClick={disk.cancel}>
            <Trans>Отменить</Trans>
          </UDButton>
        )}
      </div>
      {disk.status === 'listing' && (
        <UDText type="footnote" role="status" className="text-inkSecondary">
          <Trans>Читаем папку…</Trans>
        </UDText>
      )}
      {disk.status === 'downloading' && (
        <UDText type="footnote" role="status" className="text-inkSecondary">
          <Trans>
            Скачиваем клип {fileNumber} из {fileCount}… {percent}%
          </Trans>
        </UDText>
      )}
      {disk.failure && (
        <UDText type="footnote" role="alert" className="text-CaseErrorText">
          {t(DISK_FAILURE_MESSAGES[disk.failure])}
        </UDText>
      )}
    </div>
  );
};
